import { Box } from "@mui/material";
import { useContext } from "react";
import moment from "moment";
import { ParagraphText, UBButton } from "../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import { formatNum } from "../../../oscar-pos-core/constants";
import { DownloadIcon } from "../../../assets/icons/icon";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { getTransactionTypeConfig } from "./typeConfig";
import receiptStyles from "../UCashTransactionReceipt/styles.module.css";
import styles from "./styles.module.css";
import { sentenceCase } from "../../../constants";

/**
 * Children content for UBRightSidebar: renders a per-type transaction receipt.
 * Only transfer/deposit/recharge/bundle_recharge/bill/voucher_payment have a view;
 * returns null for any other transaction type.
 */
const TransactionDetail = ({ transaction, onDownloadReceipt, isDownloadLoading }) => {
  const [I18n] = useContext(LanguageContext);
  const config = getTransactionTypeConfig(transaction, I18n);

  if (!config) return null;
  const {
    label,
    name,
    bankLine,
    logo,
    amount,
    id,
    reasonLabel,
    commission,
    coinsEarned,
    createdAt,
    fee,
    discount,
    totalAmount,
    telco,
    details,
    showCoins,
  } = config;
  const rows = [
    showCoins && (commission || coinsEarned) && {
      label: I18n.commission || "Commission",
      value: `Rs. ${formatNum(Math.floor(Math.abs(commission)))}`,
      extra: coinsEarned ? `(${coinsEarned} ${I18n.points || "points"})` : null,
    },
    reasonLabel && {
      label: I18n.category || "Category",
      value: reasonLabel,
    },
    id != null && {
      label: I18n.transaction_id || "Transaction ID",
      value: String(id),
    },
    createdAt && {
      label: I18n.date || "Date",
      value: moment(createdAt).format("D MMMM YYYY, hh:mm A"),
    },
    fee != null && {
      label: I18n.fee || "Fee",
      value: `Rs. ${formatNum(Math.abs(fee))}`,
    },
    discount != null && {
      label: I18n.discount || "Discount",
      value: `- Rs. ${formatNum(Math.abs(discount))}`,
    },
    totalAmount != null && {
      label: I18n.total_amount || "Total amount",
      value: `Rs. ${formatNum(Math.abs(totalAmount))}`,
    },
  ].filter(Boolean);

  const handleDownload = () => {
    triggerLogEvent(analytics, "txn_detail_download_receipt", {
      id,
      type: transaction?.type,
    });
    onDownloadReceipt?.(transaction);
  };

  const onNetMinutes = details?.on_net_minutes || "";
  const offNetMinutes = details?.off_net_minutes || "";
  const sms = details?.sms || "";
  const data = details?.data || "";
  const dataUnit = details?.data_unit || "GB";
  const offerName = details?.offer_name || "";
  const currentPrice = formatNum(Math.abs(details?.price || 0));
  const oldPrice = formatNum(Math.abs(details?.old_price || transaction?.price || 0));
  const hasOldPrice = !!details?.old_price && details.old_price !== details?.price;

  const packageItems = [
    { value: onNetMinutes || "-", label: `${telco} ${I18n.mins}` || "" },
    { value: offNetMinutes || "-", label: I18n.other_net_mins || "Other net mins" },
    { value: sms || "-", label: I18n.all_net_sms || "All net SMS" },
    { value: data || "-", label: `${dataUnit}s ${I18n.data || "data"}` },
  ].filter(Boolean);
  return (
    <Box className={`transactionInvoiceBottomBox ${styles.wrapper}`}>
      {onDownloadReceipt ? (
        <UBButton
          id="transaction_detail_download_receipt"
          title={sentenceCase(I18n.download_recipt || "Download receipt")}
          iconComponent={<DownloadIcon width={16} height={16} />}
          className={`buttonTeritary ${styles.downloadButton}`}
          onClick={handleDownload}
          disabled={isDownloadLoading}
          loading={isDownloadLoading}
        />
      ) : null}
      <Box className={styles.detailCard}>
        <Box className={styles.header}>
          <ParagraphText className={styles.label}>{label}</ParagraphText>
          <ParagraphText className={receiptStyles.cardAmount}>
            {`Rs. ${formatNum(Math.abs(amount ?? 0))}`}
          </ParagraphText>
          <ParagraphText className={`fontSize14 newLightColor`}>
            {name}
            {bankLine ? ` • ${bankLine}` : ""}
          </ParagraphText>
        </Box>

        <Box className={receiptStyles.detailsSection}>
          {rows.map((row, index) => (
            <Box
              key={index}
              className={`${receiptStyles.row} ${index === rows.length - 1 ? receiptStyles.rowLast : ""
                }`}
            >
              <ParagraphText className={`fontSize14 ${receiptStyles.rowLabel}`}>
                {row.label}
              </ParagraphText>
              <ParagraphText className={`fontSize14 ${receiptStyles.rowValue}`}>
                {row.value}
                {row.extra ? " + " : ""}
                {row.extra ? (
                  <span className={styles.rowValueExtra}>{row.extra}</span>
                ) : null}
              </ParagraphText>
            </Box>
          ))}
        </Box>
      </Box>

      {details ? (
        <Box className={styles.packageBox}>
          {offerName ? (
            <ParagraphText className={styles.packageTitle}>
              {offerName}
            </ParagraphText>
          ) : null}
          <Box className={styles.packageGrid}>
            {packageItems.map((entry, index) => (
              <Box className={styles.packageGridItem} key={index}>
                <ParagraphText className={styles.packageGridValue}>
                  {entry.value}
                </ParagraphText>
                <ParagraphText className={styles.packageGridLabel}>
                  {entry.label}
                </ParagraphText>
              </Box>
            ))}
          </Box>

          <Box className={styles.packageDivider} aria-hidden="true" />

          {details.info ? (
            <ParagraphText className={styles.packageCaption}>
              {details.info}
            </ParagraphText>
          ) : null}
          {details.price != null || details.validity ? (
            <Box className={styles.packageFooter}>
              {details.price != null ? (
                <ParagraphText className={styles.packagePrice}>
                  {hasOldPrice ? (
                    <span className={styles.packageOldPrice}>{`Rs. ${oldPrice}`}</span>
                  ) : null}
                  {`Rs. ${currentPrice}`}
                </ParagraphText>
              ) : null}
              {details.validity ? (
                <ParagraphText className={styles.packageValidity}>
                  {`${I18n.validity || "Validity"}: ${details.validity}`} Days
                </ParagraphText>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ) : null}
      
    </Box>
  );
};

export default TransactionDetail;
export { SUPPORTED_TRANSACTION_TYPES } from "./typeConfig";
